import type {
	UtilityConsumptionMode,
	UtilityConsumptionPeriod,
	UtilityConsumptionReportMode,
	UtilityConsumptionReportRange,
} from "./UtilityConsumption";

export const GAS_CONSUMPTION_CHART_METRIC = {
	MMBTU: "mmbtu",
	MMBTU_PER_TON: "mmbtuPerTon",
} as const;

export type GasConsumptionChartMetric =
	(typeof GAS_CONSUMPTION_CHART_METRIC)[keyof typeof GAS_CONSUMPTION_CHART_METRIC];

export interface GasConsumptionZone {
	ZoneKey: string;
	GasZoneId: number;
	MillAssetId: number;
	MillName: string;
	Zone: string;
	IsTotal: boolean;
	LatestReadTimeUtc: Date | null;
	IsReporting: boolean;
}

export interface GasConsumptionSummary {
	IsAvailable: boolean;
	DataThroughUtc: Date | null;
	DataSinceUtc: Date | null;
	HeatValue: number;
	Rolling24HourMmbtu: number | null;
	Last7Mmbtu: number | null;
	Previous7Mmbtu: number | null;
	SevenDayChangePercent: number | null;
	ThirtyDayAverageMmbtu: number | null;
	CurrentMonthLabel: string;
	PreviousMonthLabel: string;
	PreviousYearLabel: string;
	MonthToDateMmbtu: number | null;
	LastMonthTotalMmbtu: number | null;
	YearToDateMmbtu: number | null;
	PreviousYearMmbtu: number | null;
	AverageMonthMmbtu: number | null;
	AverageMonthCount: number;
	MainTotalMmbtu: number | null;
	MainTotalTons: number | null;
	MainMmbtuPerTon: number | null;
	IsMainPartial: boolean;
	CurrentRollingSection: string | null;
	PreviousRollingSection: string | null;
	PreviousRollingStartUtc: Date | null;
	PreviousRollingEndUtc: Date | null;
	CurrentRollingMmbtuPerTon: number | null;
	CurrentRollingTons: number | null;
	PreviousRollingMmbtuPerTon: number | null;
	PreviousRollingTons: number | null;
	MainTotalCost: number | null;
	MainCostPerMcf: number | null;
	MainCostPerTon: number | null;
	IsMainCostApproximate: boolean;
	MainCostBillStart: Date | null;
	MainCostBillEnd: Date | null;
	CurrentRollingCostPerTon: number | null;
	CurrentRollingCostPerMcf: number | null;
	CurrentRollingCostBillStart: Date | null;
	CurrentRollingCostBillEnd: Date | null;
	IsCurrentRollingCostApproximate: boolean;
	PreviousRollingCostPerTon: number | null;
	IsPreviousRollingCostApproximate: boolean;
	IsRolling24HourMmbtuPartial: boolean;
	IsLast7MmbtuPartial: boolean;
	IsPrevious7MmbtuPartial: boolean;
	IsThirtyDayAverageMmbtuPartial: boolean;
	IsMonthToDateMmbtuPartial: boolean;
	IsLastMonthMmbtuPartial: boolean;
	IsYearToDateMmbtuPartial: boolean;
	IsPreviousYearMmbtuPartial: boolean;
	IsAverageMonthMmbtuPartial: boolean;
	IsMainMmbtuPartial: boolean;
	IsRolling24HourMmbtuApproximate: boolean;
	IsLast7MmbtuApproximate: boolean;
	IsPrevious7MmbtuApproximate: boolean;
	IsThirtyDayAverageMmbtuApproximate: boolean;
	IsMonthToDateMmbtuApproximate: boolean;
	IsLastMonthMmbtuApproximate: boolean;
	IsYearToDateMmbtuApproximate: boolean;
	IsPreviousYearMmbtuApproximate: boolean;
	IsAverageMonthMmbtuApproximate: boolean;
	IsMainMmbtuApproximate: boolean;
	IsCurrentRollingMmbtuApproximate: boolean;
	IsPreviousRollingMmbtuApproximate: boolean;
}

export interface GasConsumptionMillRolling {
	MillAssetId: number;
	MillName: string;
	CurrentRollingSection: string | null;
	CurrentRollingStartUtc: Date | null;
	CurrentRollingMmbtuPerTon: number | null;
	CurrentRollingTons: number | null;
	CurrentRollingCostPerTon: number | null;
}

export interface GasConsumptionMiniBucket {
	DayOrdinal: number;
	CurrentBucketStartUtc: Date | null;
	CurrentMmbtu: number | null;
	PreviousBucketStartUtc: Date;
	PreviousMmbtu: number | null;
}

export interface GasConsumptionMainBucket {
	BucketStartUtc: Date;
	ZoneKey: string;
	Mmbtu: number | null;
	MmbtuPerTon: number | null;
}

export interface GasConsumptionData {
	chartZones: GasConsumptionZone[];
	summary: GasConsumptionSummary;
	miniBuckets: GasConsumptionMiniBucket[];
	mainBuckets: GasConsumptionMainBucket[];
	millRolling: GasConsumptionMillRolling[];
}

export interface GasConsumptionRequest {
	targetId: number | null;
	zoneKeys: string[] | null;
	mode: UtilityConsumptionMode;
	startUtc: Date;
	endUtc: Date;
	waitForEveryZone?: boolean;
}

export interface UseGasConsumptionInput {
	targetId: number | null;
	enabled: boolean;
	zoneKeys: string[] | null;
	mode: UtilityConsumptionMode;
	period: UtilityConsumptionPeriod;
	waitForEveryZone?: boolean;
}

export interface GasConsumptionReportZoneRow {
	PeriodStartUtc: string;
	ZoneKey: string;
	ZoneMmbtu: number | null;
	ZoneReadings: number | null;
	ZoneExpectedReadings: number | null;
	PeriodMmbtu: number | null;
	ZoneTotalMmbtu: number | null;
	PeriodEndUtc: string | null;
	SectionId: number | null;
	SectionName: string | null;
	EventId: number | null;
	EventStartUtc: string | null;
	EventEndUtc: string | null;
	MillAssetId: number | null;
	MillName: string | null;
	MmbtuPerTon: number | null;
	MmbtuPerBillet: number | null;
	PeriodCost: number | null;
	CostPerTon: number | null;
	IsCostApproximate: boolean | null;
	IsZoneMmbtuPartial: boolean | null;
	IsZoneTotalMmbtuPartial: boolean | null;
	IsMmbtuPartial: boolean | null;
	IsMmbtuApproximate: boolean | null;
	IsCostPartial: boolean | null;
}

export interface GasConsumptionReportSummary {
	GrandTotalMmbtu: number | null;
	GrandMmbtuPerTon: number | null;
	GrandMmbtuPerBillet: number | null;
	GrandTotalCost: number | null;
	GrandCostPerTon: number | null;
	IsCostApproximate: boolean | null;
	IsMmbtuPartial: boolean | null;
	IsMmbtuApproximate: boolean | null;
	IsCostPartial: boolean | null;
}

export interface GasConsumptionReportRequest {
	targetId: number | null;
	zoneKeys: string[] | null;
	mode: UtilityConsumptionReportMode;
	sectionId: number | null;
	startUtc: Date;
	endUtc: Date;
}

export interface UseGasConsumptionReportInput {
	targetId: number | null;
	zoneKeys: string[] | null;
	areZonesLoaded: boolean;
	mode: UtilityConsumptionReportMode;
	referenceDate: Date;
	range: UtilityConsumptionReportRange;
}
